import { useRef } from 'react';
import { DownloadIcon, HakenIcon, OrdnerIcon, PlusIcon, UploadIcon, VergleichIcon } from '../icons';
import type { Objekt, Protokoll } from '../domain/types';
import { Markenlogo } from './Marke';

interface UebersichtProps {
	objekte: Objekt[];
	protokolle: Protokoll[];
	onNeuesObjekt: () => void;
	onNeueUebergabe: (objekt: Objekt) => void;
	onOeffneProtokoll: (protokoll: Protokoll) => void;
	onVergleich: (objekt: Objekt, einzug: Protokoll, auszug: Protokoll) => void;
	onExport: () => void;
	onImport: (datei: File) => void;
}

function nachDatumAbsteigend<T extends { erstelltAm: string }>(liste: T[]): T[] {
	return [...liste].sort((a, b) => b.erstelltAm.localeCompare(a.erstelltAm));
}

export function Uebersicht({
	objekte,
	protokolle,
	onNeuesObjekt,
	onNeueUebergabe,
	onOeffneProtokoll,
	onVergleich,
	onExport,
	onImport
}: UebersichtProps) {
	const importInputRef = useRef<HTMLInputElement | null>(null);
	const objekteSortiert = nachDatumAbsteigend(objekte);

	return (
		<div className="bildschirm bildschirm--uebersicht">
			<header className="uebersicht-kopf">
				<Markenlogo kompakt className="markenlogo--uebersicht" />
				<h1>Meine Objekte</h1>
				<p className="claim">Alle Übergaben an einer Adresse auf einen Blick.</p>
			</header>

			{objekteSortiert.length === 0 && (
				<p className="hinweis">Noch keine Objekte gespeichert. Lege dein erstes Objekt an.</p>
			)}

			<div className="objekt-liste">
				{objekteSortiert.map((objekt) => {
					const protokolleZumObjekt = nachDatumAbsteigend(protokolle.filter((p) => p.objektId === objekt.id));
					const einzug = protokolleZumObjekt.find((p) => p.art === 'einzug');
					const auszug = protokolleZumObjekt.find((p) => p.art === 'auszug');
					const kannVergleichen = Boolean(einzug && auszug);

					return (
						<div key={objekt.id} className="objekt-karte">
							<div className="objekt-karte-kopf">
								<OrdnerIcon width={18} height={18} />
								<div>
									<strong>{objekt.strasse || 'Ohne Adresse'}</strong>
									<span>
										{objekt.plz} {objekt.ort}
									</span>
								</div>
							</div>

							{protokolleZumObjekt.length === 0 && <p className="hinweis hinweis--klein">Noch keine Übergabe erfasst.</p>}

							<ul className="protokoll-liste">
								{protokolleZumObjekt.map((protokoll) => (
									<li key={protokoll.id}>
										<button type="button" className="protokoll-zeile" onClick={() => onOeffneProtokoll(protokoll)}>
											<span className={`protokoll-art protokoll-art--${protokoll.art}`}>
												{protokoll.art === 'einzug' ? 'Einzug' : 'Auszug'}
											</span>
											<span className="protokoll-datum">
												{new Date(protokoll.erstelltAm).toLocaleDateString('de-DE')}
											</span>
											{protokoll.abgeschlossenAm && (
												<span className="protokoll-status protokoll-status--fertig">
													<HakenIcon width={12} height={12} /> Fertig
												</span>
											)}
										</button>
									</li>
								))}
							</ul>

							<div className="objekt-karte-aktionen">
								<button type="button" className="nebenknopf" onClick={() => onNeueUebergabe(objekt)}>
									<PlusIcon width={16} height={16} />
									Neue Übergabe
								</button>
								{kannVergleichen && (
									<button type="button" className="nebenknopf" onClick={() => onVergleich(objekt, einzug!, auszug!)}>
										<VergleichIcon width={16} height={16} />
										Vergleichen
									</button>
								)}
							</div>
						</div>
					);
				})}
			</div>

			<button type="button" className="hauptknopf hauptknopf--mit-icon" onClick={onNeuesObjekt}>
				<PlusIcon width={20} height={20} />
				Neues Objekt
			</button>

			<div className="backup-leiste">
				<button type="button" className="nebenknopf" onClick={onExport}>
					<DownloadIcon width={16} height={16} />
					Daten sichern
				</button>
				<button type="button" className="nebenknopf" onClick={() => importInputRef.current?.click()}>
					<UploadIcon width={16} height={16} />
					Wiederherstellen
				</button>
				<input
					ref={importInputRef}
					type="file"
					accept="application/json"
					className="datei-eingabe-versteckt"
					onChange={(e) => {
						const datei = e.target.files?.[0];
						if (datei) onImport(datei);
						e.target.value = '';
					}}
				/>
			</div>
		</div>
	);
}
